import { useEffect, useState } from 'react';
import moment from 'moment';
import api from '../api';
import { utcToLocal } from '../utils/time';
import s from './PortalSidePanel.module.scss';

const BADGE_CLS = {
    free: s => s.bFree, trial: s => s.bTrial, basic: s => s.bBasic,
    extended: s => s.bExt, professional: s => s.bPro,
    main: s => s.bMain, other: s => s.bOther,
};

function pluralizeRu(n, one, few, many) {
    const mod10 = n % 10;
    const mod100 = n % 100;
    if (mod10 === 1 && mod100 !== 11) return one;
    if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return few;
    return many;
}

function PlanBadge({ plan, planLabels }) {
    const cls = (BADGE_CLS[plan] ? BADGE_CLS[plan](s) : null) || s.bOther;
    return <span className={`${s.badge} ${cls}`}>{planLabels[plan] || plan}</span>;
}

function UsageBar({ used, available, label }) {
    const pct = available ? Math.min((used / available) * 100, 100) : 0;
    return (
        <div className={s.usageRow}>
            <div className={s.usageTop}>
                <span>{label}</span>
                <span className={s.usageNums}>{used} / {available ?? '∞'}</span>
            </div>
            {available != null && (
                <div className={s.track}><div className={`${s.fill} ${pct >= 90 ? s.fillWarn : ''}`} style={{ width: `${pct}%` }} /></div>
            )}
        </div>
    );
}

export default function PortalSidePanel({ portalId, onClose, onSaved, plans = [] }) {
    const planLabels = { free: 'Бесплатный', other: 'Другой', ...Object.fromEntries(plans.map(p => [p.id, p.name])) };
    // В селекты выдачи/изменения — только актуальные тарифы; legacy остаются
    // в planLabels/бейджах, чтобы существующие подписки отображались как раньше
    const planOptions = plans.filter(p => p.id !== 'free' && !p.is_legacy).map(p => ({ value: p.id, label: p.name }));
    const planDefaults = Object.fromEntries(plans.map(p => [p.id, {
        analyses: p.limits.number_of_analyses,
        extUsers: p.limits.number_of_extension_users,
        resumeUpdates: p.limits.number_of_resume_updates,
    }]));

    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(true);
    const [tab, setTab] = useState('info');

    const [grantPlan, setGrantPlan] = useState('');
    const [grantDays, setGrantDays] = useState(30);
    const [grantAnalyses, setGrantAnalyses] = useState('');
    const [grantExtUsers, setGrantExtUsers] = useState('');
    const [grantResume, setGrantResume] = useState('');
    const [grantLoading, setGrantLoading] = useState(false);
    const [grantMsg, setGrantMsg] = useState(null);

    const [updEnd, setUpdEnd] = useState('');
    const [updAnalyses, setUpdAnalyses] = useState('');
    const [updExtUsers, setUpdExtUsers] = useState('');
    const [updResume, setUpdResume] = useState('');
    const [updLoading, setUpdLoading] = useState(false);
    const [updMsg, setUpdMsg] = useState(null);

    const [terminateConfirm, setTerminateConfirm] = useState(false);
    const [terminateLoading, setTerminateLoading] = useState(false);

    const [copiedTokenId, setCopiedTokenId] = useState(null);
    const copyToken = (token, id) => {
        navigator.clipboard.writeText(token).then(() => {
            setCopiedTokenId(id);
            setTimeout(() => setCopiedTokenId(c => (c === id ? null : c)), 1500);
        });
    };

    const SUSPICIOUS_CLIENTS_THRESHOLD = 3;
    const [expandedTokenId, setExpandedTokenId] = useState(null);

    // Дефолтный план — первый из списка после загрузки
    useEffect(() => {
        if (plans.length > 0 && !grantPlan) setGrantPlan(plans[0].id);
    }, [plans]);

    // Автозаполнение лимитов при выборе плана
    useEffect(() => {
        const d = planDefaults[grantPlan];
        if (d) {
            setGrantAnalyses(d.analyses != null ? String(d.analyses) : '');
            setGrantExtUsers(d.extUsers != null ? String(d.extUsers) : '');
            setGrantResume(d.resumeUpdates != null ? String(d.resumeUpdates) : '');
        } else {
            setGrantAnalyses(''); setGrantExtUsers(''); setGrantResume('');
        }
    }, [grantPlan, plans]);

    const load = () => {
        setLoading(true);
        api.get(`/admin/portals/${portalId}`)
            .then(r => {
                setDetail(r.data);
                const a = r.data.active_subscription;
                if (a) {
                    setUpdEnd(moment(a.end_date).format('YYYY-MM-DD'));
                    setUpdAnalyses(a.number_of_analyses != null ? String(a.number_of_analyses) : '');
                    setUpdExtUsers(a.number_of_extension_users != null ? String(a.number_of_extension_users) : '');
                    setUpdResume(a.number_of_resume_updates != null ? String(a.number_of_resume_updates) : '');
                }
            })
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        if (portalId) { setGrantMsg(null); setUpdMsg(null); setTerminateConfirm(false); load(); }
    }, [portalId]);

    useEffect(() => {
        if (detail) setTab('grant');
    }, [detail?.id]);

    const grantStartDate = detail?.active_subscription
        ? moment(detail.active_subscription.end_date).add(1, 'day').startOf('day')
        : null;

    const handleGrant = async () => {
        setGrantMsg(null);
        setGrantLoading(true);
        const body = { plan_type: grantPlan, days: Number(grantDays) };
        if (grantStartDate) body.start_date = grantStartDate.toISOString();
        if (grantAnalyses !== '') body.number_of_analyses = Number(grantAnalyses);
        if (grantExtUsers !== '') body.number_of_extension_users = Number(grantExtUsers);
        if (grantResume !== '') body.number_of_resume_updates = Number(grantResume);
        try {
            await api.post(`/admin/portals/${portalId}/subscription`, body);
            setGrantMsg({ ok: true, text: 'Подписка начислена' });
            onSaved?.();
            load();
        } catch (e) {
            setGrantMsg({ ok: false, text: e.response?.data?.detail || 'Ошибка' });
        } finally {
            setGrantLoading(false);
        }
    };

    const handleUpdate = async () => {
        setUpdMsg(null);
        setUpdLoading(true);
        const subId = detail?.active_subscription?.id;
        if (!subId) { setUpdMsg({ ok: false, text: 'Нет активной подписки' }); setUpdLoading(false); return; }
        const body = {};
        if (updEnd) body.end_date = new Date(updEnd).toISOString();
        if (updAnalyses !== '') body.number_of_analyses = Number(updAnalyses);
        if (updExtUsers !== '') body.number_of_extension_users = Number(updExtUsers);
        if (updResume !== '') body.number_of_resume_updates = Number(updResume);
        try {
            await api.patch(`/admin/subscription/${subId}`, body);
            setUpdMsg({ ok: true, text: 'Сохранено' });
            onSaved?.();
            load();
        } catch (e) {
            setUpdMsg({ ok: false, text: e.response?.data?.detail || 'Ошибка' });
        } finally {
            setUpdLoading(false);
        }
    };

    const handleTerminate = async () => {
        setTerminateLoading(true);
        const subId = detail?.active_subscription?.id;
        try {
            await api.patch(`/admin/subscription/${subId}`, { end_date: new Date().toISOString() });
            setTerminateConfirm(false);
            onSaved?.();
            load();
        } catch (e) {
            // оставляем confirm открытым, ошибка видна пользователю
        } finally {
            setTerminateLoading(false);
        }
    };

    const tabs = detail
        ? ['info', 'tokens', 'grant', ...(detail.active_subscription ? ['update'] : [])]
        : ['info'];
    const TAB_LABELS = { info: 'Инфо', tokens: 'Токены', grant: 'Начислить', update: 'Изменить' };

    return (
        <div className={s.panel}>
            <div className={s.panelHead}>
                <div className={s.panelHeadTop}>
                    <button className={s.closeBtn} onClick={onClose}>←</button>
                </div>
                <h2 className={s.domainTitle}>{detail?.domain ?? '...'}</h2>
            </div>

            <div className={s.scrollArea}>
            {loading ? (
                <div className={s.loader}><div className={s.spinner} /></div>
            ) : detail ? (
                <>
                    <div className={s.meta}>
                        {detail.active_subscription
                            ? <PlanBadge plan={detail.active_subscription.plan_type} planLabels={planLabels} />
                            : <span className={`${s.badge} ${s.bFree}`}>Нет подписки</span>}
                        {detail.inn && <span className={s.chip}>ИНН {detail.inn}</span>}
                        <span className={`${s.chip} ${detail.is_installed ? s.chipGreen : s.chipGray}`}>
                            {detail.is_installed ? 'Установлено' : 'Не установлено'}
                        </span>
                    </div>

                    <div className={s.tabs}>
                        {tabs.map(t => (
                            <button key={t} className={`${s.tab} ${tab === t ? s.tabActive : ''}`} onClick={() => setTab(t)}>
                                {TAB_LABELS[t]}
                            </button>
                        ))}
                    </div>

                    {tab === 'info' && (
                        <div className={s.body}>
                            <div className={s.section}>
                                <div className={s.sLabel}>Использование</div>
                                <UsageBar used={detail.usage.analysis_calls} available={detail.active_subscription?.number_of_analyses} label="AI-анализов" />
                                <UsageBar used={detail.usage.update_resume_calls} available={detail.active_subscription?.number_of_resume_updates} label="Обновлений резюме" />
                                <UsageBar used={detail.usage.extension_tokens} available={detail.active_subscription?.number_of_extension_users} label="Токенов расширения" />
                            </div>

                            {detail.active_subscription && (
                                <div className={s.section}>
                                    <div className={s.sLabel}>Активная подписка</div>
                                    <div className={s.kv}>
                                        <span>Тариф</span><span><PlanBadge plan={detail.active_subscription.plan_type} planLabels={planLabels} /></span>
                                        <span>Начало</span><span>{utcToLocal(detail.active_subscription.start_date).format('DD.MM.YYYY')}</span>
                                        <span>Окончание</span><span>{utcToLocal(detail.active_subscription.end_date).format('DD.MM.YYYY')}</span>
                                        <span>AI-анализов</span><span>{detail.active_subscription.number_of_analyses ?? '∞'}</span>
                                        <span>Польз. расширения</span><span>{detail.active_subscription.number_of_extension_users ?? '∞'}</span>
                                        <span>Обн. резюме</span><span>{detail.active_subscription.number_of_resume_updates ?? '∞'}</span>
                                    </div>
                                </div>
                            )}

                            {detail.subscriptions.length > 0 && (
                                <div className={s.section}>
                                    <div className={s.sLabel}>История подписок</div>
                                    {detail.subscriptions.map(sub => (
                                        <div key={sub.id} className={s.histRow}>
                                            <PlanBadge plan={sub.plan_type} planLabels={planLabels} />
                                            <span className={s.histDates}>{utcToLocal(sub.start_date).format('DD.MM.YY')} – {utcToLocal(sub.end_date).format('DD.MM.YY')}</span>
                                            <span className={sub.is_active ? s.active : s.inactive}>{sub.is_active ? 'активна' : 'истекла'}</span>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {detail.active_subscription && (
                                <div className={s.terminateRow}>
                                    <button className={s.terminateBtn} onClick={() => setTerminateConfirm(true)}>
                                        ✕ Завершить тариф
                                    </button>
                                </div>
                            )}

                            {terminateConfirm && (
                                <div className={s.confirmOverlay}>
                                    <div className={s.confirmBox}>
                                        <div className={s.confirmTitle}>Завершить тариф?</div>
                                        <div className={s.confirmText}>
                                            Дата окончания <strong>{planLabels[detail.active_subscription.plan_type] || detail.active_subscription.plan_type}</strong> будет
                                            изменена на сегодня. Доступ закроется немедленно.
                                        </div>
                                        <div className={s.confirmActions}>
                                            <button className={s.confirmCancel} onClick={() => setTerminateConfirm(false)}>Отмена</button>
                                            <button className={s.confirmDanger} onClick={handleTerminate} disabled={terminateLoading}>
                                                {terminateLoading ? <span className={s.spinnerDanger} /> : 'Завершить'}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {tab === 'tokens' && (
                        <div className={s.body}>
                            {detail.extension_tokens.length === 0 ? (
                                <div className={s.tokensEmpty}>Токены расширения ещё не выдавались</div>
                            ) : (
                                <div className={s.section}>
                                    {detail.extension_tokens.map(t => {
                                        const suspicious = t.clients.length >= SUSPICIOUS_CLIENTS_THRESHOLD;
                                        const expanded = expandedTokenId === t.id;
                                        return (
                                            <div key={t.id} className={`${s.tokenCard} ${suspicious ? s.tokenCardSuspicious : ''}`}>
                                                <button
                                                    type="button"
                                                    className={s.tokenCardHead}
                                                    onClick={() => setExpandedTokenId(expanded ? null : t.id)}
                                                >
                                                    <div className={s.tokenMain}>
                                                        <span className={s.tokenName}>{t.token_name?.trim() || 'Без названия'}</span>
                                                        <span className={s.tokenResp}>{t.responsible_name || '—'}</span>
                                                    </div>
                                                    <div className={s.tokenCardRight}>
                                                        {suspicious && (
                                                            <span className={s.suspiciousBadge} title={`Заходили с ${t.clients.length} разных клиентов`}>
                                                                ⚠ Стоит проверить
                                                            </span>
                                                        )}
                                                        <span className={s.clientsCount}>{t.clients.length} {pluralizeRu(t.clients.length, 'клиент', 'клиента', 'клиентов')}</span>
                                                        <span className={`${s.expandArrow} ${expanded ? s.expandArrowOpen : ''}`}>▼</span>
                                                    </div>
                                                </button>

                                                <div className={`${s.tokenCardBodyWrap} ${expanded ? s.tokenCardBodyWrapOpen : ''}`}>
                                                    <div className={s.tokenCardBodyInner}>
                                                        <div className={s.tokenCardBody}>
                                                            <button
                                                                type="button"
                                                                className={`${s.tokenValue} ${copiedTokenId === t.id ? s.tokenValueCopied : ''}`}
                                                                onClick={() => copyToken(t.access_token, t.id)}
                                                                title="Скопировать токен"
                                                            >
                                                                <span className={s.tokenValueText}>{t.access_token}</span>
                                                                <span className={s.tokenValueHint}>{copiedTokenId === t.id ? 'Скопировано' : 'Копировать'}</span>
                                                            </button>
                                                            {t.clients.length > 0 ? (
                                                                <div className={s.clientsCards}>
                                                                    {t.clients.map(c => (
                                                                        <div key={c.id} className={s.clientCard}>
                                                                            <div className={s.clientCardMain}>
                                                                                {c.browser}{c.browser_version ? ` ${c.browser_version}` : ''}
                                                                                {' · '}
                                                                                {c.device ? c.device : `${c.os}${c.os_version ? ` ${c.os_version}` : ''}`}
                                                                                {c.accept_language ? ` · ${c.accept_language}` : ''}
                                                                            </div>
                                                                            <div className={s.clientCardChips}>
                                                                                <span className={s.clientChip}>
                                                                                    {c.hit_count} {pluralizeRu(c.hit_count, 'вход', 'входа', 'входов')}
                                                                                </span>
                                                                                <span className={s.clientChip}>{utcToLocal(c.last_seen_at).format('DD.MM HH:mm')}</span>
                                                                                <span className={s.clientChip}>{c.last_ip || '—'}</span>
                                                                            </div>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            ) : (
                                                                <div className={s.clientsEmpty}>Ещё не использовался</div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    )}

                    {tab === 'grant' && (
                        <div className={s.body}>
                            <div className={s.formFields}>
                                <Field label="Тариф">
                                    <select value={grantPlan} onChange={e => setGrantPlan(e.target.value)}>
                                        {planOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                                    </select>
                                </Field>
                                <Field label="Срок (дней)">
                                    <input type="number" min={1} value={grantDays} onChange={e => setGrantDays(e.target.value)} />
                                </Field>
                                <Field label="AI-анализов в месяц">
                                    <input type="number" min={0} value={grantAnalyses} onChange={e => setGrantAnalyses(e.target.value)} placeholder="∞" />
                                </Field>
                                <Field label="Польз. расширения">
                                    <input type="number" min={0} value={grantExtUsers} onChange={e => setGrantExtUsers(e.target.value)} placeholder="∞" />
                                </Field>
                                <Field label="Обновлений резюме">
                                    <input type="number" min={0} value={grantResume} onChange={e => setGrantResume(e.target.value)} placeholder="∞" />
                                </Field>
                            </div>
                            {grantMsg && <Msg {...grantMsg} />}
                            <Btn onClick={handleGrant} loading={grantLoading}>Начислить подписку</Btn>
                            {grantStartDate && (
                                <div className={s.grantNote}>
                                    Подписка начнётся после окончания текущей —&nbsp;
                                    <strong>{grantStartDate.format('DD.MM.YYYY')}</strong>
                                </div>
                            )}
                        </div>
                    )}

                    {tab === 'update' && (
                        <div className={s.body}>
                            <div className={s.formFields}>
                                <Field label="Дата окончания">
                                    <input type="date" value={updEnd} onChange={e => setUpdEnd(e.target.value)} />
                                </Field>
                                <Field label="AI-анализов в месяц">
                                    <input type="number" min={0} value={updAnalyses} onChange={e => setUpdAnalyses(e.target.value)} placeholder="∞" />
                                </Field>
                                <Field label="Польз. расширения">
                                    <input type="number" min={0} value={updExtUsers} onChange={e => setUpdExtUsers(e.target.value)} placeholder="∞" />
                                </Field>
                                <Field label="Обновлений резюме">
                                    <input type="number" min={0} value={updResume} onChange={e => setUpdResume(e.target.value)} placeholder="∞" />
                                </Field>
                            </div>
                            {updMsg && <Msg {...updMsg} />}
                            <Btn onClick={handleUpdate} loading={updLoading}>Сохранить</Btn>
                        </div>
                    )}
                </>
            ) : (
                <div className={s.loader}>Портал не найден</div>
            )}
            </div>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <div className={s.field}>
            <label>{label}</label>
            {children}
        </div>
    );
}

function Msg({ ok, text }) {
    return <div className={ok ? s.msgOk : s.msgErr}>{text}</div>;
}

function Btn({ onClick, loading, children }) {
    return (
        <button className={s.btn} onClick={onClick} disabled={loading}>
            {loading ? <span className={s.spinner} /> : children}
        </button>
    );
}
